import React from 'react';
import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';
import { Button } from './Button';
import { CHECKOUT_URL, HERO_IMAGE, HERO_IMAGE_ALT } from '../data/offer';
import { trackCheckout } from '../utils/tracking';
const negatives = ['Not a random collection of cocktail videos.', 'Not a pile of slides.', 'Not a text-heavy manual you have to turn into classes.', 'Not a pile of resources you have to organize yourself.'];
const teachingFlow = ['OPEN THE SYSTEM.', 'CHOOSE THE TOPIC.', 'SHOW THE VISUAL.', 'TEACH THE CLASS.', 'RUN THE PRACTICE.', 'CHECK PROGRESS.'];
const teachingAreas = [{
  number: '01',
  step: 'BARTENDING FUNDAMENTALS, BAR TOOLS & GLASSWARE',
  detail: 'The workstation, essential tools, glassware, ice, measuring, shaking, stirring, straining and muddling.'
}, {
  number: '02',
  step: 'CLASSIC COCKTAILS, INTERNATIONAL RECIPES & PREPARATION',
  detail: 'Cocktail specs, balance, drink families, classic and international recipes, methods and garnishes.'
}, {
  number: '03',
  step: 'ADVANCED MIXOLOGY, MOLECULAR & CREATIVE COCKTAILS',
  detail: 'Infusions, syrups, clarification, carbonation, foams, flavor building and signature cocktail creation.'
}, {
  number: '04',
  step: 'SPIRITS, LIQUEURS, FLAVOR PROFILES & SENSORY ANALYSIS',
  detail: 'How spirits are made, major spirit categories, liqueurs, bitters, mixers and professional tasting.'
}, {
  number: '05',
  step: 'PRESENTATION, GARNISHING, MENU DESIGN & OPERATIONS',
  detail: 'Garnish techniques, glass presentation, menu development, costing, inventory and service workflows.'
}, {
  number: '06',
  step: 'BARTENDING INSTRUCTION, PROGRAMS & ASSESSMENTS',
  detail: 'Lesson plans, practical mixing exercises, service simulations, course design, practical exams and certificates.'
}];
const ease = [0.23, 1, 0.32, 1] as const;
export function SolutionReveal() {
  return <section className="py-20 px-4 bg-[#FBF6EF] relative overflow-hidden" data-id="element-282" data-mp="2hqz3je22h5e9">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-1 bg-[#C9A24B]" data-id="element-283" data-mp="2hp4js32j7jbr" />

      <div className="max-w-4xl mx-auto text-center" data-id="element-284" data-mp="2cqbe2wthkwm5">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.3,
        ease
      }} data-id="element-285" data-mp="2qihijlna1f5r">
          <p className="text-sm md:text-base font-semibold text-[#84651F] mb-4" data-id="element-286" data-mp="1yteg8beyaevr">
            The Complete Visual Bar Training System for Bartending Instructors, Bar Trainers &amp; Mixology Educators
          </p>
          <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4 text-[#1F4D3A]" data-id="element-287" data-mp="27umbslq311m5">
            BARTENDING &amp; MIXOLOGY INSTRUCTOR <span className="text-[#9C7726]" data-id="element-288" data-mp="22waxnysr84y4">ATLAS</span>
          </h2>
          <p className="text-base md:text-lg text-[#1F4D3A] font-semibold mb-6" data-id="element-289" data-mp="3j6dc4hhjuqyb">
            A Complete 6-IN-1 Visual Bartending &amp; Mixology Teaching System
          </p>
          <img src={HERO_IMAGE} alt={HERO_IMAGE_ALT} className="w-full max-w-sm h-auto mx-auto mb-6 drop-shadow-xl" loading="lazy" data-id="element-290" data-mp="3mp2mt2pu3dz4" />
          <p className="text-base md:text-lg text-[#84651F] font-semibold mb-10" data-id="element-291" data-mp="3csr7s4yv0a0z">
            6 PROFESSIONAL VOLUMES · 300 VISUAL TRAINING PAGES · 5 EXCLUSIVE BONUSES
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 text-left" data-id="element-292" data-mp="9cag2geymfp5">
          <motion.div initial={{
          opacity: 0,
          x: -12
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease
        }} className="space-y-3 bg-white/60 p-6 rounded-2xl border border-slate-200" data-id="element-293" data-mp="eac8hry9zrda">
            {negatives.map(statement => <div key={statement} className="flex items-start gap-3" data-id="element-294" data-mp="s9bne7vzkrw9">
                <X className="text-[#1F4D3A]/50 shrink-0 mt-1" size={18} data-id="element-295" data-mp="x1buckah4l2c" />
                <p className="text-slate-700/80 text-sm" data-id="element-296" data-mp="34kerwcy2sf4e">{statement}</p>
              </div>)}
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 12
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease
        }} className="bg-white p-6 rounded-2xl border-2 border-[#1F4D3A]" data-id="element-297" data-mp="30ijsjdzpgbt9">
            <div className="flex items-start gap-3" data-id="element-298" data-mp="3o849xsg7n10f">
              <Check className="text-[#9C7726] shrink-0 mt-1" size={20} data-id="element-299" data-mp="3o7ks0bqk8cme" />
              <div className="space-y-3" data-id="element-300" data-mp="3jaccyk0pkopd">
                <p className="text-[#1F4D3A] font-medium text-base leading-relaxed" data-id="element-301" data-mp="3jbnd89uy3ebq">
                  BARTENDING &amp; MIXOLOGY INSTRUCTOR ATLAS is a complete visual training system for bartending
                  instructors, bar trainers, mixology educators and bar managers — from beginner to advanced.
                </p>
                <p className="text-[#84651F] font-semibold text-sm leading-relaxed" data-id="element-302" data-mp="6297osh00j7m">
                  Every page gives you an illustration, labeled diagram or process chart, a step-by-step
                  breakdown, a ❌ common mistake vs. ✅ correct technique comparison, a teaching cue, a drill,
                  a quick check and an Easy / Standard / Advanced variant — all in one place.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="mt-10" data-id="element-303" data-mp="wcru34yhhot0">
          <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1.5" data-id="element-304" data-mp="wbqfwrm68m72">
            {teachingFlow.map(line => <li key={line} className="text-lg md:text-xl font-extrabold tracking-wide text-[#1F4D3A]" data-id="element-305" data-mp="119rxf83y8dgf">
                {line}
              </li>)}
          </ul>
          <p className="mt-8 text-lg md:text-xl font-bold text-[#1F4D3A]" data-id="element-306" data-mp="1b0cv8tbstdya">
            Make Your Next Bartending Class Easier to Prepare and More Visual to Teach.
          </p>
          <div className="flex justify-center mt-5" data-id="element-307" data-mp="1k1ksd82kw8qs">
            <Button href={CHECKOUT_URL} onLinkClick={trackCheckout} className="text-sm sm:text-lg py-3 sm:py-4 px-5 sm:px-8" data-id="element-308" data-mp="1k09jwuy6uctc">
              GET MY COMPLETE 6-IN-1 BAR TRAINING SYSTEM NOW!
            </Button>
          </div>
        </div>

        <div className="mt-12" data-id="element-309" data-mp="3pxepf1yk58o8">
          <p className="text-[#84651F] text-sm font-semibold uppercase tracking-widest mb-5" data-id="element-310" data-mp="3l4tyc871d9oe">
            The System Covers Six Essential Bar Training Areas
          </p>
          <ol className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3" data-id="element-311" data-mp="3q6ms7f0vfvw">
            {teachingAreas.map(item => <li key={item.step} className="flex flex-col items-center px-3 py-4 bg-white rounded-xl border border-slate-200" data-id="element-312" data-mp="coicm782e9na">
                <span className="text-sm font-bold text-[#84651F] mb-1" data-id="element-313" data-mp="8lcanbrzj4mm">{item.number}</span>
                <span className="text-lg md:text-xl font-extrabold tracking-tight leading-tight text-[#1F4D3A]" data-id="element-314" data-mp="2ei7yb6420lut">
                  {item.step}
                </span>
                <span className="text-sm text-slate-700/80 mt-1" data-id="element-315" data-mp="20bwhxaggwvea">{item.detail}</span>
              </li>)}
          </ol>
          <p className="mt-8 text-sm md:text-base font-bold tracking-widest text-[#1F4D3A]" data-id="element-316" data-mp="24eayfnwnzj2e">
            SIX AREAS. SIX COMPLETE VOLUMES. ONE PROFESSIONAL TRAINING SYSTEM.
          </p>
        </div>
      </div>
    </section>;
}